// SCR-STAFF-874 (AP-INV-02؛ US-INV-023، 032، 033، 041): هشدارهای انبار (کمبود، موجودی منفی، تأمین کابینت، انقضا، فراخوان، مصرف دیررس، BOM)
// با رسیدگی؛ فراخوان بچ با دلیل و مرجع و فهرست دریافت‌کنندگان (فقط با inv.recall_recipient.view؛ مشاهده در Audit ثبت می‌شود — AC-INV-033.2).
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { unwrap, type InvAlertView, type InvRecallView, type InvRecipientView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, ListPage, Money, faDigits, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { fromMilli, stockKeys, useItemOptions, useWarehouses, warehouseName } from './stockCommon';

export const SCR_STAFF_874 = 'SCR-STAFF-874';

type TabId = 'alerts' | 'recalls';

const milliText = (milli: number | string | null | undefined) => (milli === null || milli === undefined ? '—' : faDigits(String(fromMilli(milli))));

export function AlertsRecallsPage() {
  const { t } = useTranslation('inv');
  const [tab, setTab] = useState<TabId>('alerts');
  return (
    <ListPage
      screenId={SCR_STAFF_874}
      titleKey="inv:alerts.title"
      subtitleKey="inv:alerts.subtitle"
      filters={
        <Tabs value={tab} onChange={(_event, value: TabId) => setTab(value)} aria-label={t('inv:alerts.title')}>
          <Tab value="alerts" label={t('inv:alerts.tabs.alerts')} data-testid="alerts-tab-alerts" />
          <Tab value="recalls" label={t('inv:alerts.tabs.recalls')} data-testid="alerts-tab-recalls" />
        </Tabs>
      }
    >
      {tab === 'alerts' ? <AlertsTable /> : <RecallsPanel />}
    </ListPage>
  );
}

function AlertsTable() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const warehouses = useWarehouses();
  const write = useWriteAccess('inv.warehouse.manage');
  const alerts = useQuery({
    queryKey: stockKeys.alerts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/alerts', { params: { query: { includeResolved: false } } })),
  });
  const resolve = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/inventory/alerts/{id}:resolve', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: stockKeys.alerts(session?.tenantId) }),
  });

  const columns: DataTableColumn<InvAlertView>[] = [
    { id: 'kind', headerKey: 'inv:alerts.columns.kind', cell: (row) => <span data-testid={`alert-${row.kind}`}>{t(`inv:alert_kind.${row.kind}`)}</span> },
    { id: 'item', headerKey: 'inv:alerts.columns.item', cell: (row) => row.itemName ?? '—' },
    { id: 'batch', headerKey: 'inv:alerts.columns.batch', cell: (row) => <bdi dir="ltr">{row.batchNumber ?? '—'}</bdi> },
    { id: 'warehouse', headerKey: 'inv:alerts.columns.warehouse', cell: (row) => (row.warehouseId ? warehouseName(warehouses.data?.items, row.warehouseId) : '—') },
    { id: 'quantity', headerKey: 'inv:alerts.columns.quantity', cell: (row) => milliText(row.quantityMilli) },
    { id: 'value', headerKey: 'inv:alerts.columns.value', cell: (row) => (row.value ? <Money amountMinor={row.value.amount} currency={row.value.currency} /> : '—') },
    { id: 'raised', headerKey: 'inv:alerts.columns.raised_at', cell: (row) => <JalaliDate value={row.raisedAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'inv:alerts.columns.actions',
      cell: (row) => (
        <Button size="small" disabled={!write.canWrite || resolve.isPending} onClick={() => resolve.mutate(row.id)} data-testid={`alert-resolve-${row.id}`}>
          {t('inv:alerts.resolve')}
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      captionKey="inv:alerts.caption"
      columns={columns}
      rows={alerts.data}
      getRowId={(row) => row.id}
      isLoading={alerts.isPending}
      error={alerts.error}
      onRetry={() => void alerts.refetch()}
      emptyTitleKey="inv:alerts.empty"
    />
  );
}

function RecallsPanel() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('inv.recall.manage');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<InvRecallView | null>(null);
  const recalls = useQuery({
    queryKey: stockKeys.recalls(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/recalls')),
  });
  const canSeeRecipients = access.can('inv.recall_recipient.view');

  const columns: DataTableColumn<InvRecallView>[] = [
    { id: 'item', headerKey: 'inv:recalls.columns.item', cell: (row) => row.itemName },
    { id: 'batch', headerKey: 'inv:recalls.columns.batch', cell: (row) => <bdi dir="ltr">{row.batchNumber}</bdi> },
    { id: 'reason', headerKey: 'inv:recalls.columns.reason', cell: (row) => row.reason },
    { id: 'reference', headerKey: 'inv:recalls.columns.reference', cell: (row) => <bdi dir="ltr">{row.reference}</bdi> },
    { id: 'blocked', headerKey: 'inv:recalls.columns.blocked', cell: (row) => <span data-testid={`recall-blocked-${row.batchNumber}`}>{milliText(row.blockedMilli)}</span> },
    { id: 'recipients', headerKey: 'inv:recalls.columns.recipients', cell: (row) => faDigits(String(row.recipientCount)) },
    { id: 'date', headerKey: 'inv:recalls.columns.date', cell: (row) => <JalaliDate value={row.recalledAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'inv:recalls.columns.actions',
      cell: (row) =>
        canSeeRecipients ? (
          <Button size="small" onClick={() => setViewing(row)} data-testid={`recall-recipients-${row.batchNumber}`}>
            {t('inv:recalls.view_recipients')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack spacing={1}>
      <WriteNotice write={write} />
      <Stack direction="row">
        <Button variant="contained" color="error" startIcon={<AddIcon />} disabled={!write.canWrite} onClick={() => setCreating(true)} data-testid="recall-create">
          {t('inv:recalls.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="inv:recalls.caption"
        columns={columns}
        rows={recalls.data?.items}
        getRowId={(row) => row.id}
        isLoading={recalls.isPending}
        error={recalls.error}
        onRetry={() => void recalls.refetch()}
        emptyTitleKey="inv:recalls.empty"
      />
      {creating && <RecallDialog onClose={() => setCreating(false)} />}
      {viewing && <RecipientsDialog recall={viewing} onClose={() => setViewing(null)} />}
    </Stack>
  );
}

function RecallDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const items = useItemOptions();
  const [itemId, setItemId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const batches = useQuery({
    queryKey: ['inventory', 'items', session?.tenantId, itemId, 'batches', false],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items/{id}/batches', { params: { path: { id: itemId } } })),
    enabled: Boolean(itemId),
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/recalls', {
          body: { batchId, reason: reason.trim(), reference: reference.trim() },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stockKeys.recalls(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: stockKeys.alerts(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-874-RECALL" titleKey="inv:recalls.create_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={Boolean(batchId || reason)} isSubmitting={save.isPending} submitError={save.error}>
      <Alert severity="warning">{t('inv:recalls.warning')}</Alert>
      <TextField select required label={t('inv:recalls.columns.item')} value={itemId} onChange={(event) => { setItemId(event.target.value); setBatchId(''); }} slotProps={{ htmlInput: { 'data-testid': 'recall-item' } }}>
        {(items.data?.items ?? [])
          .filter((item) => item.tracksBatch)
          .map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
      </TextField>
      <TextField select required label={t('inv:recalls.columns.batch')} value={batchId} onChange={(event) => setBatchId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'recall-batch' } }}>
        {(batches.data?.items ?? []).map((batch) => (
          <MenuItem key={batch.id} value={batch.id}>
            {batch.batchNumber}
          </MenuItem>
        ))}
      </TextField>
      <TextField required label={t('inv:recalls.columns.reason')} value={reason} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'recall-reason' } }} />
      <TextField required label={t('inv:recalls.columns.reference')} value={reference} onChange={(event) => setReference(event.target.value)} helperText={t('inv:recalls.reference_help')} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'recall-reference' } }} />
    </FormDialog>
  );
}

function RecipientsDialog({ recall, onClose }: { recall: InvRecallView; onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const recipients = useQuery({
    queryKey: [...stockKeys.recalls(session?.tenantId), recall.id, 'recipients'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/recalls/{id}/recipients', { params: { path: { id: recall.id } } })),
  });
  const columns: DataTableColumn<InvRecipientView>[] = [
    { id: 'file', headerKey: 'inv:recalls.recipient_columns.file_number', cell: (row) => <bdi dir="ltr">{row.fileNumber ?? '—'}</bdi> },
    { id: 'name', headerKey: 'inv:recalls.recipient_columns.name', cell: (row) => row.displayName ?? '—' },
    { id: 'source', headerKey: 'inv:recalls.recipient_columns.source', cell: (row) => t(`inv:purpose.${row.source}`) },
    { id: 'date', headerKey: 'inv:recalls.recipient_columns.date', cell: (row) => <JalaliDate value={row.consumedOn} /> },
    { id: 'quantity', headerKey: 'inv:recalls.recipient_columns.quantity', cell: (row) => milliText(row.quantityMilli) },
  ];
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth data-screen-id="SCR-STAFF-874-RECIPIENTS">
      <DialogTitle>{t('inv:recalls.recipients_title', { batch: recall.batchNumber })}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Alert severity="info">{t('inv:recalls.recipients_audit')}</Alert>
          <DataTable
            captionKey="inv:recalls.recipients_caption"
            columns={columns}
            rows={recipients.data?.items}
            getRowId={(row) => `${row.patientId ?? 'sale'}-${row.consumedOn}-${row.quantityMilli}`}
            isLoading={recipients.isPending}
            error={recipients.error}
            emptyTitleKey="inv:recalls.recipients_empty"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
